(() => {
  if (customElements.get('main-product-addons-bundle')) return;

  const CARD_SELECTOR = '[data-bundle-card]';
  const ACTION_SELECTOR = [
    '[data-add-to-bundle]',
    '[data-bundle-add]',
    '[data-add-to-cart]',
    '[data-product-add]',
    '.product-card__select',
    '.product-card__select-button',
    '.card-product__select',
    '.product-card__add',
    '.product-form__submit',
    '.quick-add__submit',
    'button[name="add"]',
    'button[type="submit"]',
    'input[type="submit"]',
    'product-form button',
  ].join(',');
  const OPTION_SELECTOR = [
    '[aria-haspopup]',
    '[data-option-position]',
    '[data-option-value]',
    '[data-variant-option]',
    '[data-swatch]',
    '[role="radio"]',
    '.swatch',
    '.swatch-input',
    '.product-option',
    '.product-card__media',
  ].join(',');

  class MainProductAddonsBundle extends HTMLElement {
    constructor() {
      super();

      this.selectedMain = null;
      this.selectedAddons = [];
      this.maxAddons = 3;
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onPanelsChanged = this.onPanelsChanged.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onMobileViewportChange = this.onMobileViewportChange.bind(this);
      this.onViewportResize = this.onViewportResize.bind(this);
      this.onViewportScroll = this.onViewportScroll.bind(this);
      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeyDown = this.onTabKeyDown.bind(this);
      this.onCartDrawerMutation = this.onCartDrawerMutation.bind(this);
      this.onCartDrawerOpenSignal = this.onCartDrawerOpenSignal.bind(this);
    }

    connectedCallback() {
      this.maxAddons = Math.min(3, Math.max(1, this.numberSetting('maxAddons', 3)));
      this.tierOneQuantity = this.numberSetting('tier1Addons', 2);
      this.tierOneDiscount = this.numberSetting('tier1Discount', 15);
      this.tierTwoQuantity = this.numberSetting('tier2Addons', 3);
      this.tierTwoDiscount = this.numberSetting('tier2Discount', 20);
      this.preventDuplicateProducts = this.dataset.preventDuplicateItems !== 'false';

      this.addEventListener('click', this.onClick, true);
      this.addEventListener('submit', this.onSubmit, true);
      this.addEventListener('change', this.onChange, true);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('cart:open', this.onCartDrawerOpenSignal, true);

      for (const eventName of ['variant:change', 'variant-change', 'variantChange', 'theme:variant:change']) {
        this.addEventListener(eventName, this.onVariantChange);
      }

      const panels = this.querySelector('.tabs__panels');
      if (panels && typeof MutationObserver !== 'undefined') {
        this.panelsObserver = new MutationObserver(this.onPanelsChanged);
        this.panelsObserver.observe(panels, { childList: true, subtree: true });
      }

      this.bundleSheet = this.querySelector('[data-bundle-sheet]');
      this.mobileViewport = window.matchMedia('(max-width: 749px)');
      this.mobileViewport.addEventListener?.('change', this.onMobileViewportChange);
      window.addEventListener('resize', this.onViewportResize, { passive: true });
      window.addEventListener('scroll', this.onViewportScroll, { passive: true });

      this.tabsWrapper = this.closest('.section__wrapper');
      this.tabsWrapper?.addEventListener('click', this.onTabClick);
      this.tabsWrapper?.addEventListener('keydown', this.onTabKeyDown);
      this.observeCartDrawer();
      this.syncMobileStickyMetrics();
      this.syncTabState();

      this.syncVariantLevelCards();
      this.renderBundle();
      this.updateBundleSheetMode(true);
      this.switchToRole('main');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick, true);
      this.removeEventListener('submit', this.onSubmit, true);
      this.removeEventListener('change', this.onChange, true);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('cart:open', this.onCartDrawerOpenSignal, true);
      this.mobileViewport?.removeEventListener?.('change', this.onMobileViewportChange);
      window.removeEventListener('resize', this.onViewportResize);
      window.removeEventListener('scroll', this.onViewportScroll);
      this.tabsWrapper?.removeEventListener('click', this.onTabClick);
      this.tabsWrapper?.removeEventListener('keydown', this.onTabKeyDown);
      if (this.stickyMetricsFrame) cancelAnimationFrame(this.stickyMetricsFrame);
      if (this.cartDrawerSignalTimer) clearTimeout(this.cartDrawerSignalTimer);

      for (const eventName of ['variant:change', 'variant-change', 'variantChange', 'theme:variant:change']) {
        this.removeEventListener(eventName, this.onVariantChange);
      }

      this.panelsObserver?.disconnect();
      this.cartDrawerObserver?.disconnect();
      this.cartDrawerHostObserver?.disconnect();
      this.classList.remove('is-cart-drawer-open');
      this.setBodySheetState(false);
      this.setBodyMobileState(false);
    }

    numberSetting(name, fallback) {
      const value = Number.parseInt(this.dataset[name], 10);
      return Number.isFinite(value) ? value : fallback;
    }

    onPanelsChanged() {
      this.syncTabState();
      this.syncVariantLevelCards();
      this.syncCardButtons();
      this.syncSelectedCards();
      this.syncMobileStickyMetrics();
    }

    onMobileViewportChange() {
      this.updateBundleSheetMode(false);
      this.syncCartDrawerState();
      this.syncMobileStickyMetrics();
    }

    onViewportResize() {
      this.syncMobileStickyMetrics();
    }

    onViewportScroll() {
      if (!this.mobileViewport?.matches || this.stickyMetricsFrame) return;

      this.stickyMetricsFrame = requestAnimationFrame(() => {
        this.stickyMetricsFrame = null;
        this.syncMobileStickyMetrics();
      });
    }

    onKeyDown(event) {
      if (event.key !== 'Escape' || !this.mobileViewport?.matches) return;
      if (!this.bundleSheet?.classList.contains('is-mobile-expanded')) return;

      event.preventDefault();
      this.closeMobileSheet(true);
    }

    updateBundleSheetMode(initializing) {
      if (!this.bundleSheet) return;

      if (this.mobileViewport?.matches) {
        this.bundleSheet.open = false;
        this.bundleSheet.classList.remove('is-mobile-expanded');
        this.syncMobileSheetControl(false);
        this.setBodySheetState(false);
        this.setBodyMobileState(true);
        return;
      }

      this.bundleSheet.open = true;
      this.bundleSheet.classList.remove('is-mobile-expanded');
      this.syncMobileSheetControl(true);
      this.setBodyMobileState(false);
      if (!initializing) this.setBodySheetState(false);
    }

    openMobileSheet() {
      if (!this.bundleSheet || !this.mobileViewport?.matches) return;

      this.syncCartDrawerState();
      if (this.cartDrawerOpen) return;

      this.sheetReturnFocus = this.querySelector('[data-bundle-sheet-toggle]');
      this.bundleSheet.open = true;
      this.bundleSheet.classList.add('is-mobile-expanded');
      this.syncMobileSheetControl(true);
      this.setBodySheetState(true);

      requestAnimationFrame(() => this.querySelector('[data-bundle-sheet-close]')?.focus({ preventScroll: true }));
    }

    closeMobileSheet(restoreFocus) {
      if (!this.bundleSheet || !this.mobileViewport?.matches) return;

      this.bundleSheet.open = false;
      this.bundleSheet.classList.remove('is-mobile-expanded');
      this.syncMobileSheetControl(false);
      this.setBodySheetState(false);

      if (restoreFocus) {
        requestAnimationFrame(() => this.sheetReturnFocus?.focus({ preventScroll: true }));
      }
    }

    setBodySheetState(open) {
      document.body?.classList.toggle('bundle-sheet-open', Boolean(open));
    }

    setBodyMobileState(mobile) {
      document.body?.classList.toggle('bundle-sheet-mobile', Boolean(mobile));
    }

    syncMobileSheetControl(expanded) {
      const isExpanded = Boolean(expanded);
      const toggle = this.querySelector('[data-bundle-sheet-toggle]');
      const toggleLabel = toggle?.querySelector('[data-bundle-sheet-toggle-label]');

      this.bundleSheet?.setAttribute('aria-expanded', String(isExpanded));
      toggle?.setAttribute('aria-expanded', String(isExpanded));
      toggle?.setAttribute('aria-label', isExpanded ? 'Hide bundle details' : 'View bundle details');
      if (toggleLabel) toggleLabel.textContent = isExpanded ? 'Hide' : 'View';
    }

    isCartDrawerOpen(drawer = this.cartDrawer()) {
      if (!drawer || drawer.hidden || drawer.hasAttribute('hidden') || drawer.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      return drawer.hasAttribute('open')
        || drawer.matches('.active, .is-open, .open')
        || drawer.getAttribute('aria-hidden') === 'false';
    }

    syncCartDrawerState(drawer = this.observedCartDrawer || this.cartDrawer()) {
      const isOpen = this.isCartDrawerOpen(drawer);
      this.cartDrawerOpen = isOpen;
      this.classList.toggle('is-cart-drawer-open', Boolean(this.mobileViewport?.matches && isOpen));

      if (isOpen && this.mobileViewport?.matches) this.closeMobileSheet(false);
    }

    onCartDrawerMutation() {
      this.observeCartDrawer();
    }

    onCartDrawerOpenSignal() {
      if (!this.mobileViewport?.matches) return;

      this.cartDrawerOpen = true;
      this.classList.add('is-cart-drawer-open');
      this.closeMobileSheet(false);

      if (this.cartDrawerSignalTimer) clearTimeout(this.cartDrawerSignalTimer);
      this.cartDrawerSignalTimer = setTimeout(() => {
        this.cartDrawerSignalTimer = null;
        this.observeCartDrawer();
      }, 0);
    }

    observeCartDrawer() {
      const drawer = this.cartDrawer();

      if (drawer !== this.observedCartDrawer) {
        this.cartDrawerObserver?.disconnect();
        this.cartDrawerHostObserver?.disconnect();
        this.observedCartDrawer = drawer;

        if (drawer && typeof MutationObserver !== 'undefined') {
          this.cartDrawerObserver = new MutationObserver(this.onCartDrawerMutation);
          this.cartDrawerObserver.observe(drawer, {
            attributes: true,
            attributeFilter: ['hidden', 'open', 'class', 'aria-hidden'],
          });

          const drawerHost = drawer.closest('.shopify-section') || drawer.parentElement;
          if (drawerHost) {
            this.cartDrawerHostObserver = new MutationObserver(this.onCartDrawerMutation);
            this.cartDrawerHostObserver.observe(drawerHost, { childList: true, subtree: true });
          }
        }
      }

      this.syncCartDrawerState(drawer);
    }

    bundleTabs() {
      return [...(this.tabsWrapper?.querySelectorAll('.bundle-tab[role="tab"]') || [])];
    }

    syncMobileStickyMetrics() {
      if (!this.tabsWrapper) return;

      if (!this.mobileViewport?.matches) {
        this.tabsWrapper.style.removeProperty('--bundle-mobile-sticky-top');
        this.tabsWrapper.style.removeProperty('--bundle-mobile-tabs-height');
        this.tabsWrapper.style.removeProperty('--bundle-mobile-sticky-background');
        return;
      }

      const headerSelectors = [
        '.shopify-section-group-header-group',
        '[data-section-type="header"]',
        '#shopify-section-header',
        'sticky-header',
        'header-component',
        '.header-wrapper',
        'header[role="banner"]',
      ].join(',');
      const headerCandidates = [...new Set(document.querySelectorAll(headerSelectors))];
      let stickyTop = 0;

      for (const element of headerCandidates) {
        if (this.tabsWrapper.contains(element)) continue;

        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        const isHeaderGroup = element.matches('.shopify-section-group-header-group');
        const isPositioned = style.position === 'fixed' || style.position === 'sticky' || isHeaderGroup;
        const coversViewport = rect.width >= window.innerWidth * 0.5;
        const occupiesTop = rect.bottom > 0 && rect.top < window.innerHeight * 0.25;

        if (isPositioned && coversViewport && occupiesTop) stickyTop = Math.max(stickyTop, rect.bottom);
      }

      if (stickyTop === 0) {
        const roots = [document.documentElement, document.body];
        const variableNames = ['--header-height', '--sticky-header-height', '--header-group-height'];
        for (const root of roots) {
          const styles = getComputedStyle(root);
          for (const variableName of variableNames) {
            const value = Number.parseFloat(styles.getPropertyValue(variableName));
            if (Number.isFinite(value) && value > 0) stickyTop = Math.max(stickyTop, value);
          }
        }
      }

      const tabsContainer = this.tabsWrapper.querySelector('[data-bundle-collection-tabs]');
      const sectionRoot = this.closest('.section');
      const sectionBackground = sectionRoot ? getComputedStyle(sectionRoot).backgroundColor : '';
      const transparentBackground = !sectionBackground || sectionBackground === 'transparent'
        || sectionBackground === 'rgba(0, 0, 0, 0)';

      this.tabsWrapper.style.setProperty('--bundle-mobile-sticky-top', `${Math.max(0, Math.round(stickyTop))}px`);
      this.tabsWrapper.style.setProperty(
        '--bundle-mobile-tabs-height',
        `${Math.max(68, Math.ceil(tabsContainer?.getBoundingClientRect().height || 0))}px`,
      );
      if (!transparentBackground) {
        this.tabsWrapper.style.setProperty('--bundle-mobile-sticky-background', sectionBackground);
      } else {
        this.tabsWrapper.style.removeProperty('--bundle-mobile-sticky-background');
      }
    }

    switchFromCrossCollectionLink(trigger) {
      const targetIndex = trigger?.dataset.bundleNextTab;
      if (targetIndex == null) return;

      const targetTab = this.bundleTabs().find((tab) => tab.dataset.index === targetIndex);
      if (!targetTab) return;

      targetTab.click();

      const finishNavigation = (attempt = 0) => {
        this.syncMobileStickyMetrics();
        const tabsContainer = this.tabsWrapper?.querySelector('[data-bundle-collection-tabs]');
        const panelId = targetTab.getAttribute('aria-controls');
        const targetPanel = panelId ? document.getElementById(panelId) : null;

        if (!targetPanel && attempt < 5) {
          requestAnimationFrame(() => finishNavigation(attempt + 1));
          return;
        }

        const scrollTarget = this.mobileViewport?.matches && targetPanel ? targetPanel : tabsContainer;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        scrollTarget?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        targetTab.focus({ preventScroll: true });
      };

      requestAnimationFrame(() => finishNavigation());
    }

    updateStepState(activeTab) {
      const mainStep = this.tabsWrapper?.querySelector('[data-bundle-step-main]');
      const addonStep = this.tabsWrapper?.querySelector('[data-bundle-step-addon]');
      if (!mainStep || !addonStep) return;

      const currentTab = activeTab
        || this.bundleTabs().find((tab) => tab.classList.contains('active'))
        || this.bundleTabs()[0];
      const activeRole = currentTab?.dataset.bundleRole === 'addon' ? 'addon' : 'main';
      const hasMain = Boolean(this.selectedMain);
      const mainLabel = mainStep.querySelector('[data-bundle-step-label]');

      if (mainLabel) mainLabel.textContent = hasMain ? '✓ MAIN SELECTED' : 'CHOOSE MAIN GEAR';
      mainStep.classList.toggle('is-complete', hasMain);
      mainStep.classList.toggle('is-active', activeRole === 'main');
      addonStep.classList.toggle('is-active', activeRole === 'addon');

      for (const [step, current] of [[mainStep, activeRole === 'main'], [addonStep, activeRole === 'addon']]) {
        if (current) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      }
    }

    syncTabState(activeTab) {
      const tabs = this.bundleTabs();
      const active = activeTab || tabs.find((tab) => tab.classList.contains('active')) || tabs[0];

      for (const tab of tabs) {
        const selected = tab === active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        tab.classList.toggle('active', selected);

        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel && this.tabsWrapper?.contains(panel)) {
          panel.hidden = !selected;
          panel.setAttribute('aria-hidden', String(!selected));
          panel.classList.toggle('active', selected);
        }
      }

      this.updateStepState(active);
    }

    onTabClick(event) {
      const tab = event.target.closest('.bundle-tab[role="tab"]');
      if (!tab || !this.tabsWrapper?.contains(tab)) return;

      requestAnimationFrame(() => this.syncTabState(tab));
    }

    onTabKeyDown(event) {
      const tab = event.target.closest('.bundle-tab[role="tab"]');
      if (!tab) return;

      const tabs = this.bundleTabs();
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      tabs[nextIndex].focus();
      tabs[nextIndex].click();
    }

    onClick(event) {
      const trigger = event.target.closest('button, input[type="submit"], [role="button"], [data-add-to-bundle], [data-bundle-add], [data-bundle-sheet-summary]');
      if (!trigger || !this.contains(trigger)) return;

      const nextTabTrigger = trigger.closest('[data-bundle-next-tab]');
      if (nextTabTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.switchFromCrossCollectionLink(nextTabTrigger);
        return;
      }

      if (trigger.closest('[data-bundle-sheet-close]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeMobileSheet(true);
        return;
      }

      if (trigger.closest('[data-bundle-sheet-summary]') && this.mobileViewport?.matches) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.bundleSheet?.classList.contains('is-mobile-expanded')) this.closeMobileSheet(false);
        else this.openMobileSheet();
        return;
      }

      const removeButton = trigger.closest('.products-bundle-selection__bar-item-remove');
      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.removeSlot(removeButton.closest('[data-bundle-slot]'));
        return;
      }

      if (trigger.closest('.products-bundle-selection__bar-add')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
        return;
      }

      const card = trigger.closest(CARD_SELECTOR);
      if (!card || !this.isSelectionButton(trigger)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.selectCard(card, trigger);
    }

    onSubmit(event) {
      const card = event.target.closest(CARD_SELECTOR);
      if (!card) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.selectCard(card, event.submitter);
    }

    onChange(event) {
      const card = event.target.closest(CARD_SELECTOR);
      if (!card) return;

      queueMicrotask(() => {
        this.syncVariantLevelCards();
        this.refreshSelectedVariant(card);
      });
    }

    onVariantChange(event) {
      const card = event.target.closest(CARD_SELECTOR);
      if (!card) return;

      this.syncVariantLevelCards();
      this.refreshSelectedVariant(card);
    }

    isSelectionButton(button) {
      if (button.closest(OPTION_SELECTOR)) return false;
      if (button.matches(ACTION_SELECTOR)) return true;
      if (button.matches('[aria-controls], [data-modal-open], [data-drawer-open]')) return false;

      return button.tagName === 'BUTTON' && !button.matches('[type="reset"]');
    }

    selectionButtons(card) {
      return [...card.querySelectorAll('button, input[type="submit"], [data-add-to-bundle], [data-bundle-add]')]
        .filter((button) => this.isSelectionButton(button));
    }

    variantsFor(card) {
      if (card.bundleVariants) return card.bundleVariants;

      try {
        card.bundleVariants = JSON.parse(card.querySelector('[data-bundle-variants]')?.textContent || '[]');
      } catch {
        card.bundleVariants = [];
      }

      return card.bundleVariants;
    }

    formFor(card, trigger) {
      return trigger?.form || card.querySelector('form[action*="/cart/add"], product-form form, form');
    }

    variantFor(card, trigger, providedVariant) {
      const variants = this.variantsFor(card);
      const fixedVariantId = card.dataset.fixedVariantId
        || card.dataset.variantId
        || trigger?.dataset.variantId;

      if (!fixedVariantId) return null;
      return variants.find((variant) => String(variant.id) === String(fixedVariantId)) || null;
    }

    syncVariantLevelCards() {
      for (const card of this.querySelectorAll(CARD_SELECTOR)) {
        const variant = this.variantFor(card);
        if (!variant) continue;

        const variantId = String(variant.id);
        const form = this.formFor(card);
        const variantInput = form?.elements?.namedItem('id') || card.querySelector('[name="id"]');
        if (variantInput) variantInput.value = variantId;

        card.dataset.variantId = variantId;
        card.dataset.selectedVariantId = variantId;

        for (const button of this.selectionButtons(card)) {
          button.dataset.variantId = variantId;
          const label = button.querySelector('.btn__text') || button.querySelector('[data-button-text]') || button;
          if (variant.available === false) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            if (label.textContent?.trim() !== 'Sold out') label.textContent = 'Sold out';
          } else {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            if (label.textContent?.trim() !== 'Add') label.textContent = 'Add';
          }
        }
      }
    }

    itemFor(card, trigger, providedVariant) {
      const variant = this.variantFor(card, trigger, providedVariant);
      if (!variant) return null;

      return {
        productId: String(card.dataset.productId),
        variantId: String(variant.id),
        variant,
        price: Number(variant.price),
        title: card.dataset.productTitle || '',
        image: this.imageUrlFor(card, variant),
        role: card.dataset.bundleRole || card.closest('[data-bundle-role]')?.dataset.bundleRole,
        form: this.formFor(card, trigger),
        card,
      };
    }

    imageUrlFor(card, variant) {
      const candidates = [
        variant?.featured_image?.src,
        variant?.image?.src,
        variant?.image,
        card?.dataset.productFeaturedImage,
        card?.dataset.productImage,
      ];

      for (const candidate of candidates) {
        if (typeof candidate !== 'string') continue;
        const url = candidate.trim();
        if (!url || url === 'undefined' || url === 'null') continue;

        if (url.startsWith('/') || url.startsWith('data:image/') || url.startsWith('blob:')) {
          return url;
        }

        try {
          const parsedUrl = new URL(url);
          if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') return parsedUrl.href;
        } catch {
          // Ignore invalid URLs so the slot can render its safe placeholder.
        }
      }

      return '';
    }

    sameProduct(left, right) {
      if (!left || !right) return false;

      return String(left.productId) === String(right.productId)
        || String(left.variantId) === String(right.variantId);
    }

    selectCard(card, trigger) {
      const role = card.dataset.bundleRole;

      const item = this.itemFor(card, trigger);
      if (!item || item.variant.available === false || !Number.isFinite(item.price)) {
        this.showError('This item is sold out.');
        return false;
      }

      this.clearError();

      if (role === 'main') {
        if (this.selectedAddons.some((addon) => this.sameProduct(addon, item))) {
          this.showNotice(this.dataset.duplicateMessage || 'This product is already selected as an add-on.');
          return false;
        }

        this.selectedMain = item;
        this.renderBundle();
        this.switchToRole('addon');
        return true;
      }

      if (role !== 'addon') return false;

      if (this.sameProduct(this.selectedMain, item)) {
        this.showNotice(this.dataset.duplicateMessage || 'The main product cannot also be an add-on.');
        return false;
      }

      if (this.preventDuplicateProducts && this.selectedAddons.some((addon) => this.sameProduct(addon, item))) {
        this.showNotice(this.dataset.duplicateMessage || 'This product is already selected.');
        return false;
      }

      if (this.selectedAddons.length >= this.maxAddons) {
        this.showNotice('Remove an add-on to choose another.');
        return false;
      }

      this.selectedAddons.push(item);
      this.renderBundle();
      return true;
    }

    refreshSelectedVariant(card, providedVariant) {
      const current = this.itemFor(card, null, providedVariant);
      if (!current) return;

      card.dataset.selectedVariantId = current.variantId;

      if (current.role === 'main' && this.selectedMain?.productId === current.productId) {
        this.selectedMain = current;
        this.renderBundle();
        return;
      }

      if (current.role !== 'addon') return;

      const addonIndex = this.selectedAddons.findIndex((addon) => addon.productId === current.productId);
      if (addonIndex !== -1) {
        this.selectedAddons[addonIndex] = current;
        this.renderBundle();
      }
    }

    removeSlot(slot) {
      if (!slot) return;

      const index = Number.parseInt(slot.dataset.bundleSlot, 10);
      if (index === 0) {
        this.selectedMain = null;
        this.renderBundle();
        this.switchToRole('main');
        return;
      }

      this.selectedAddons.splice(index - 1, 1);
      this.renderBundle();
    }

    currentDiscount() {
      if (!this.selectedMain || this.selectedAddons.length < this.tierOneQuantity) return 0;
      if (this.selectedAddons.length >= this.tierTwoQuantity) return this.tierTwoDiscount;
      if (this.selectedAddons.length >= this.tierOneQuantity) return this.tierOneDiscount;
      return 0;
    }

    totals() {
      const discount = this.currentDiscount();
      const selectedItems = [this.selectedMain, ...this.selectedAddons].filter(Boolean);
      const regularSubtotal = selectedItems.reduce((total, item) => {
        const compareAtPrice = Number(item.variant?.compare_at_price);
        const regularPrice = Number.isFinite(compareAtPrice) && compareAtPrice > item.price
          ? compareAtPrice
          : item.price;
        return total + regularPrice;
      }, 0);
      const currentSubtotal = selectedItems.reduce((total, item) => total + item.price, 0);
      const addonCurrentSubtotal = this.selectedAddons.reduce((total, addon) => total + addon.price, 0);
      const tierDiscount = Math.round((addonCurrentSubtotal * discount) / 100);
      const bundleTotal = currentSubtotal - tierDiscount;
      const bundleSavings = Math.max(0, regularSubtotal - bundleTotal);

      return {
        discount,
        regularSubtotal,
        currentSubtotal,
        addonCurrentSubtotal,
        tierDiscount,
        bundleTotal,
        bundleSavings,
      };
    }

    formatMoneyText(cents) {
      const format = this.dataset.moneyFormat;
      let formattedMoney = '';
      const formatters = [
        [window.FoxTheme?.Currency, window.FoxTheme?.Currency?.formatMoney],
        [window.FoxTheme?.utils, window.FoxTheme?.utils?.formatMoney],
        [window.theme?.Currency, window.theme?.Currency?.formatMoney],
        [window.theme, window.theme?.formatMoney],
        [window.Shopify, window.Shopify?.formatMoney],
        [window, window.formatMoney],
      ];

      for (const [owner, formatter] of formatters) {
        if (typeof formatter !== 'function') continue;

        try {
          formattedMoney = formatter.call(owner, cents, format);
          break;
        } catch {
          // Try the next formatter before falling back to Intl.NumberFormat.
        }
      }

      if (!formattedMoney) {
        try {
          formattedMoney = new Intl.NumberFormat(this.dataset.locale || undefined, {
            style: 'currency',
            currency: this.dataset.currency || 'USD',
          }).format(cents / 100);
        } catch {
          formattedMoney = (cents / 100).toFixed(2);
        }
      }

      let decodedMoney = String(formattedMoney);

      // Decode at most two entity layers without attaching any parsed nodes.
      for (let pass = 0; pass < 2; pass += 1) {
        const decoder = document.createElement('textarea');
        decoder.innerHTML = decodedMoney;
        const decoded = decoder.value;
        if (decoded === decodedMoney) break;
        decodedMoney = decoded;
      }

      // Extract text from formatter markup in an inert fragment. Only this text
      // is returned and later assigned through textContent.
      const fragment = document.createElement('template');
      fragment.innerHTML = decodedMoney;
      const moneyText = (fragment.content.textContent || '').replace(/\s+/g, ' ').trim();
      const currency = (this.dataset.currency || '').trim().toUpperCase();
      const includesCurrencyCode = currency && moneyText.toUpperCase().includes(currency);

      return currency && !includesCurrencyCode ? `${moneyText} ${currency}` : moneyText;
    }

    renderBundle() {
      const totals = this.totals();
      const selectedCount = Number(Boolean(this.selectedMain)) + this.selectedAddons.length;

      const count = this.querySelector('.products-bundle-selection__count');
      if (count) count.textContent = String(selectedCount);

      this.renderTotals(totals);

      this.renderMessages(totals.discount);
      this.renderSlots();
      this.renderSubmitButton();
      this.renderMobileTier();
      this.syncCardButtons();
      this.syncSelectedCards();
      this.updateStepState();
    }

    renderTotals(totals) {
      const total = this.querySelector('.products-bundle-selection__bar-total');
      const regular = this.querySelector('[data-bundle-regular]');
      const savings = this.querySelector('[data-bundle-savings]');

      if (total) total.textContent = this.formatMoneyText(totals.bundleTotal);
      if (regular) regular.textContent = this.formatMoneyText(totals.regularSubtotal);
      if (savings) savings.textContent = this.formatMoneyText(totals.bundleSavings);

      const hasSavings = totals.bundleSavings > 0;
      this.querySelector('[data-bundle-regular-row]')?.classList.toggle('hidden', !hasSavings);
      this.querySelector('[data-bundle-savings-row]')?.classList.toggle('hidden', !hasSavings);
    }

    renderMessages(discount) {
      const message = this.querySelector('[data-bundle-message]');
      const nextTier = this.querySelector('[data-bundle-next-tier]');
      if (!message || !nextTier) return;

      nextTier.textContent = '';
      nextTier.classList.add('hidden');

      if (!this.selectedMain) {
        message.textContent = 'Choose a main product';
        return;
      }

      if (this.selectedAddons.length < this.tierOneQuantity || discount === 0) {
        const remaining = Math.max(1, this.tierOneQuantity - this.selectedAddons.length);
        message.textContent = remaining === 1
          ? `Add 1 more add-on to save ${this.tierOneDiscount}%`
          : `Add ${remaining} add-ons to save ${this.tierOneDiscount}%`;
        return;
      }

      if (this.selectedAddons.length >= this.tierTwoQuantity) {
        message.textContent = `${discount}% off all ${this.selectedAddons.length} add-ons`;
        return;
      }

      message.textContent = `${discount}% off both add-ons · Add one more to save ${this.tierTwoDiscount}%`;
    }

    renderMobileTier() {
      const tier = this.querySelector('[data-bundle-mobile-tier]');
      if (!tier) return;

      if (!this.selectedMain) {
        tier.textContent = 'Choose a main product';
      } else if (this.selectedAddons.length < this.tierOneQuantity) {
        tier.textContent = `${this.tierOneQuantity - this.selectedAddons.length} to ${this.tierOneDiscount}% off`;
      } else if (this.selectedAddons.length < this.tierTwoQuantity) {
        tier.textContent = `1 to ${this.tierTwoDiscount}% off`;
      } else {
        tier.textContent = `${this.tierTwoDiscount}% unlocked`;
      }
    }

    renderSlotPlaceholder(content, index) {
      content.replaceChildren();

      if (index === 0) {
        const placeholder = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        placeholder.setAttribute('class', 'bundle-main-placeholder');
        placeholder.setAttribute('viewBox', '0 0 64 64');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.dataset.bundleSlotPlaceholder = '';

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M13 15h38a5 5 0 0 1 5 5v29H8V20a5 5 0 0 1 5-5Zm-1 30h40V21a2 2 0 0 0-2-2H14a2 2 0 0 0-2 2v24Zm7-7 8-9 7 7 5-5 9 9H16l3-2Z');
        path.setAttribute('fill', 'currentColor');
        placeholder.append(path);
        content.append(placeholder);
        return;
      }

      const placeholder = document.createElement('div');
      placeholder.className = 'flex items-center justify-center text-center';
      placeholder.dataset.bundleSlotPlaceholder = '';

      if (index === 1) {
        placeholder.classList.add('bundle-addon-plus');
        placeholder.textContent = '+';
      } else {
        placeholder.classList.add('bundle-slot-placeholder--discount');
        const label = document.createElement('span');
        label.textContent = index === 2 ? `-${this.tierOneDiscount}%` : `-${this.tierTwoDiscount}%`;
        placeholder.append(label);
      }

      content.append(placeholder);
    }

    renderSlots() {
      const items = [
        this.selectedMain,
        this.selectedAddons[0] || null,
        this.selectedAddons[1] || null,
        this.selectedAddons[2] || null,
      ];

      for (const slot of this.querySelectorAll('[data-bundle-slot]')) {
        const index = Number.parseInt(slot.dataset.bundleSlot, 10);
        const item = items[index];
        const visualSlot = slot.querySelector('.products-bundle-selection__bar-item');
        const content = slot.querySelector('.products-bundle-selection__bar-item-content');
        const removeButton = slot.querySelector('.products-bundle-selection__bar-item-remove');
        if (!content || !removeButton) continue;

        content.replaceChildren();
        visualSlot?.querySelector('[data-bundle-tier-badge]')?.remove();
        slot.classList.toggle('is-placeholder', !item);
        visualSlot?.classList.toggle('is-placeholder', !item);
        removeButton.hidden = !item;
        removeButton.disabled = !item;

        if (!item) {
          content.setAttribute('aria-label', index === 0 ? 'Main product' : `Add-on slot ${index}`);
          this.renderSlotPlaceholder(content, index);
          slot.removeAttribute('data-selected-variant-id');
          continue;
        }

        slot.dataset.selectedVariantId = item.variantId;
        content.setAttribute('aria-label', item.title);
        removeButton.setAttribute('aria-label', `Remove ${item.title}`);

        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          image.loading = 'lazy';
          image.addEventListener('error', () => {
            image.remove();
            visualSlot?.querySelector('[data-bundle-tier-badge]')?.remove();
            this.renderSlotPlaceholder(content, index);
          }, { once: true });
          content.append(image);
        } else {
          this.renderSlotPlaceholder(content, index);
        }

        if (index > 1 && visualSlot && item.image) {
          const badge = document.createElement('span');
          badge.className = 'bundle-slot-tier-badge';
          badge.dataset.bundleTierBadge = '';
          badge.textContent = index === 2 ? `-${this.tierOneDiscount}%` : `-${this.tierTwoDiscount}%`;
          visualSlot.append(badge);
        }

      }
    }

    renderSubmitButton() {
      const button = this.querySelector('.products-bundle-selection__bar-add');
      const label = button?.querySelector('.btn__text');
      if (!button || !label) return;

      if (!this.selectedMain) {
        button.disabled = true;
        label.textContent = 'Choose a main product';
      } else if (this.selectedAddons.length === 0) {
        button.disabled = false;
        label.textContent = 'Add main product to cart';
      } else if (this.selectedAddons.length === 1) {
        button.disabled = false;
        label.textContent = 'Add selected items to cart';
      } else {
        button.disabled = false;
        label.textContent = 'Add bundle to cart';
      }
    }

    syncCardButtons() {
      for (const card of this.querySelectorAll(`${CARD_SELECTOR}[data-bundle-role="addon"]`)) {
        for (const button of this.selectionButtons(card)) {
          if (button.dataset.bundleDisabledWithoutMain === 'true') {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            delete button.dataset.bundleDisabledWithoutMain;
          }
        }
      }
    }

    syncSelectedCards() {
      const selectedItems = [this.selectedMain, ...this.selectedAddons].filter(Boolean);

      for (const card of this.querySelectorAll(CARD_SELECTOR)) {
        const selected = selectedItems.some((item) => {
          return item.role === card.dataset.bundleRole
            && item.variantId === (card.dataset.fixedVariantId || card.dataset.variantId);
        });

        card.classList.toggle('is-selected', selected);
        card.dataset.bundleSelected = String(selected);

        for (const button of this.selectionButtons(card)) {
          button.setAttribute('aria-pressed', String(selected));
        }
      }
    }

    switchToRole(role) {
      const wrapper = this.closest('.section__wrapper');
      const tab = wrapper?.querySelector(`.tabs__btn[data-bundle-role="${role}"]`);
      if (!tab) return;

      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => tab.click());
      } else {
        tab.click();
      }
    }

    showNotice(message) {
      const status = this.querySelector('[data-bundle-message]');
      if (status) status.textContent = message;
    }

    showError(message) {
      const error = this.querySelector('.product-form__error-message');
      if (!error) return;

      error.textContent = message;
      error.hidden = false;
    }

    clearError() {
      const error = this.querySelector('.product-form__error-message');
      if (!error) return;

      error.textContent = '';
      error.hidden = true;
    }

    cartItem(item) {
      const payload = { id: Number(item.variantId), quantity: 1 };
      if (!item.form || typeof FormData === 'undefined') return payload;

      const formData = new FormData(item.form);
      const properties = {};

      for (const [name, value] of formData.entries()) {
        if (name === 'quantity') {
          const quantity = Number.parseInt(String(value), 10);
          if (Number.isFinite(quantity) && quantity > 0) payload.quantity = quantity;
        }

        if (name === 'selling_plan' && value) {
          payload.selling_plan = String(value);
        }

        const property = name.match(/^properties\[(.+)\]$/);
        if (property && value !== '') properties[property[1]] = value;
      }

      if (Object.keys(properties).length > 0) payload.properties = properties;
      return payload;
    }

    cartDrawer() {
      return document.querySelector('cart-drawer, [is="cart-drawer"], cart-notification');
    }

    cartSections(drawer) {
      const themeSections = typeof drawer?.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender()
          .map((section) => section.section || section.id)
          .filter(Boolean)
        : [];

      return themeSections.length > 0 ? themeSections : ['cart-drawer', 'cart-icon-bubble'];
    }

    async addToCart() {
      const canSubmit = Boolean(this.selectedMain);
      if (!canSubmit || this.addingToCart) return;

      const selectedItems = [this.selectedMain, ...this.selectedAddons];
      if (selectedItems.some((item) => item.variant.available === false)) {
        this.showError('A selected item is sold out.');
        return;
      }

      const button = this.querySelector('.products-bundle-selection__bar-add');
      const buttonLabel = button?.querySelector('.btn__text');
      const drawer = this.cartDrawer();
      const sections = this.cartSections(drawer);
      const payload = { items: selectedItems.map((item) => this.cartItem(item)) };

      payload.sections = sections;
      payload.sections_url = window.location.pathname;

      this.addingToCart = true;
      this.clearError();
      button?.setAttribute('aria-busy', 'true');
      button?.classList.add('loading');
      if (button) button.disabled = true;
      if (buttonLabel) buttonLabel.textContent = 'Adding…';

      try {
        const response = await fetch(this.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const cartData = await response.json();

        if (!response.ok || cartData.status) {
          throw new Error(cartData.description || cartData.message || 'Unable to add the selected set to your cart.');
        }

        if (!Array.isArray(cartData.items) || cartData.items.length !== payload.items.length) {
          throw new Error('Shopify did not confirm every selected item. Please review your cart and try again.');
        }

        const cartState = await this.fetchCartState();
        this.updateCart(cartData, drawer, cartState);
      } catch (error) {
        this.showError(error.message || 'Unable to add the selected set to your cart.');
      } finally {
        this.addingToCart = false;
        button?.removeAttribute('aria-busy');
        button?.classList.remove('loading');
        this.renderSubmitButton();
      }
    }

    async fetchCartState() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    }

    updateHeaderCartCount(cartState) {
      if (!Number.isFinite(Number(cartState?.item_count))) return;

      const count = String(cartState.item_count);
      for (const element of document.querySelectorAll('[data-cart-count], .cart-count-bubble, .header__cart-count')) {
        element.textContent = count;
        element.classList.toggle('hidden', cartState.item_count === 0);
        element.removeAttribute('hidden');
      }
    }

    updateCart(cartData, drawer, cartState) {
      const detail = {
        cart: cartState || cartData,
        cartData,
        cartState,
        source: 'main-product-addons-bundle',
      };

      const activeDrawer = drawer || this.cartDrawer();

      if (activeDrawer) {
        try {
          if (typeof activeDrawer.renderContents === 'function') {
            activeDrawer.renderContents(cartData);
          } else if (typeof activeDrawer.render === 'function') {
            activeDrawer.render(cartData);
          }
        } catch {
          // The theme events below can still refresh an existing drawer.
        }
      }

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
      }

      const foxEvents = window.FoxTheme?.events;
      if (typeof foxEvents?.emit === 'function') {
        foxEvents.emit('cart:add', detail);
        foxEvents.emit('cart:refresh', detail);
        foxEvents.emit('cart:open', detail);
      }

      for (const eventName of ['cart:add', 'cart:refresh', 'cart:updated', 'cart:update', 'cart:open']) {
        document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
      }

      this.updateHeaderCartCount(cartState);

      const refreshedDrawer = this.cartDrawer() || activeDrawer;
      if (refreshedDrawer) {
        try {
          if (typeof refreshedDrawer.open === 'function') {
            refreshedDrawer.open();
          } else if (typeof refreshedDrawer.show === 'function') {
            refreshedDrawer.show();
          } else {
            refreshedDrawer.setAttribute('open', '');
            refreshedDrawer.classList.add('active', 'is-open');
          }
        } catch {
          // cart:open remains the theme-compatible fallback for this drawer.
        }
        return;
      }

      this.showNotice('Items added to cart. Open the cart from the header.');
    }
  }

  customElements.define('main-product-addons-bundle', MainProductAddonsBundle);
})();
